import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useDashboardStore } from "@/stores/useDashboardStore";

import { NewsDto } from "../..";
import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../api/resources";
import Dialog from "../../../../../../components/Dialog/Dialog";
import { DialogWidth } from "../../../../../../components/Dialog/types/enums";
import { ALERT_STATUS } from "../../../../../../context/alert/types";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import useRefreshDashboardData from "../../../../../../shared/hooks/useRefreshDashboardData";
import useResponse from "../../../../../../shared/hooks/useResponse";

interface PublishNewsDialogProps {
  isDialogOpen: boolean;
  onClose: () => void;
  newsData: NewsDto | null;
  isReleaseNotes: boolean;
}

const PublishNewsDialog = ({
  isDialogOpen,
  onClose,
  newsData,
  isReleaseNotes,
}: PublishNewsDialogProps) => {
  const { patchData } = useAxiosPrivate();
  const { handleResponse, handleCommonError } = useResponse();
  const { refreshNewsData } = useRefreshDashboardData();
  const { t } = useTranslation();

  const setScrollOffsetNumber = useDashboardStore(
    (state) => state.setNewsScrollOffset,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const publishNewsTitle = isReleaseNotes
    ? t("Dialog##publish release notes")
    : t("Dialog##publish news");

  const publishNewsDescription = isReleaseNotes
    ? t("Dialog##publish release notes description")
    : t("Dialog##publish news description");

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await patchData(`${ApiResources.NewsItems}/${newsData?.id}/Publish`).then(
        () => {
          refreshNewsData();
        },
      );
      setScrollOffsetNumber(0);
      handleResponse(ALERT_STATUS.Success, t("Alert##news published"));
    } catch (error) {
      handleCommonError(error, t);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <Dialog
      data-testid="publish-news-dialog"
      title={publishNewsTitle}
      description={publishNewsDescription}
      width={DialogWidth.MinWidth}
      extendToMaxWidth
      isActionsSeparator
      open={isDialogOpen}
      close={onClose}
      submit={handleSubmit}
      actions={
        <DialogActions
          onClose={onClose}
          isLoading={isLoading}
          isreadyToConfirm={true}
          confirmationButtonText={t("Button##accept")}
          confirmationButtonTestId="publish-news-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="close-publish-news"
        />
      }
    ></Dialog>
  );
};

export default PublishNewsDialog;
